import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { MessageSquare, Users, Info, Gauge } from 'lucide-react';
import { Audience, AudienceSpeed } from '@/store/videoFeatures/types/audience';
import { t } from '@/utils/i18n';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface ConfigurationTabProps {
  audience: Audience;
  updateAudience: (key: string, value: any) => void;
  isDisabled: boolean;
}

export const ConfigurationTab: React.FC<ConfigurationTabProps> = ({
  audience,
  updateAudience,
  isDisabled
}) => {
  useLanguage();
  return (
    <div className="space-y-4">
      {/* Card 1: Contagem */}
      <div className={cn('bg-white border border-[#E4E7EC] rounded-[14px] p-4 transition-all', isDisabled && 'opacity-50 pointer-events-none blur-[1px]')}>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-9 h-9 rounded-[10px] bg-[#EEF4FF] flex items-center justify-center flex-shrink-0">
            <Users className="w-[18px] h-[18px] text-[#1D4FFF]" strokeWidth={2} />
          </div>
          <span className="text-[14px] font-semibold text-[#1A1A1A]">
            {t('audience.count.title')}
          </span>
        </div>
        <div className="space-y-3">
          {/* Contagem Base */}
          <div className="space-y-1.5">
            <Label htmlFor="viewers-base" className="text-[12px] font-medium flex items-center gap-1">
              <span>{translateInterfaceText("Base (opcional)")}</span>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="h-3 w-3 text-muted-foreground cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="text-xs">{translateInterfaceText("Valor inicial do contador de audiência")}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </Label>
            <Input
              id="viewers-base"
              type="number"
              min={0}
              className="h-9 text-sm"
              value={audience.viewersCount ?? ''}
              onChange={(e) => {
                const value = e.target.value;
                updateAudience('viewersCount', value === '' ? undefined : parseInt(value) || 0);
              }}
              placeholder={translateInterfaceText("Ex: 150")}
            />
          </div>

          <p className="text-[11px] text-muted-foreground">
            {translateInterfaceText("Deixe vazio para exibir apenas a mensagem, sem contador numérico.")}</p>
        </div>
      </div>

      {/* Card 2: Mensagem Exibida - Sempre aberto */}
      <div className={cn('bg-white border border-[#E4E7EC] rounded-[14px] p-4 transition-all', isDisabled && 'opacity-50 pointer-events-none blur-[1px]')}>
        {/* Header sem caret - sempre visível */}
        <div className="flex items-center gap-3 mb-4">
          <div className="w-9 h-9 rounded-[10px] bg-[#EEF4FF] flex items-center justify-center flex-shrink-0">
            <MessageSquare className="w-[18px] h-[18px] text-[#1D4FFF]" strokeWidth={2} />
          </div>
          <span className="text-[14px] font-semibold text-[#1A1A1A]">
            {translateInterfaceText("Mensagem Exibida")}</span>
        </div>

        {/* Corpo - sempre visível */}
        <div className="space-y-3">
          {/* Campo de mensagem personalizada - sempre visível */}
          <div className="space-y-1.5">
            <Label htmlFor="audience-message" className="text-[12px] font-medium flex items-center justify-between">
              <span>{translateInterfaceText("Mensagem personalizada")}</span>
              <span className="text-[11px] text-muted-foreground">
                {(audience.message || '').length}/120
              </span>
            </Label>
            <div className="relative">
              <MessageSquare className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="audience-message"
                type="text"
                maxLength={120}
                className="h-10 pl-10 text-sm"
                value={audience.message || ''}
                onChange={(e) => {
                  updateAudience('message', e.target.value);
                }}
                placeholder={translateInterfaceText("Ex: Ao vivo agora")}
              />
            </div>
            <p className="text-[11px] text-muted-foreground">
              {audience.viewersCount
                ? translateInterfaceText("O número definido em \"Base\" será exibido automaticamente após a mensagem.")
                : translateInterfaceText("Defina um valor em \"Base\" para exibir um contador numérico junto com a mensagem.")}
            </p>
          </div>

        </div>
      </div>

      {/* Card 3: Velocidade da Simulação */}
      <div className={cn('bg-white border border-[#E4E7EC] rounded-[14px] p-4 transition-all', isDisabled && 'opacity-50 pointer-events-none blur-[1px]')}>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-9 h-9 rounded-[10px] bg-[#EEF4FF] flex items-center justify-center flex-shrink-0">
            <Gauge className="w-[18px] h-[18px] text-[#1D4FFF]" strokeWidth={2} />
          </div>
          <div>
            <span className="text-[14px] font-semibold text-[#1A1A1A] block">
              {translateInterfaceText("Velocidade da Simulação")}</span>
            <span className="text-[11px] text-muted-foreground">
              {translateInterfaceText("Defina o ritmo de alteração da audiência para deixar a simulação mais natural.")}</span>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2.5">
          {([
            { value: 'slow' as AudienceSpeed, label: translateInterfaceText("Lenta"), desc: translateInterfaceText("Mais natural e estável") },
            { value: 'medium' as AudienceSpeed, label: translateInterfaceText("Média"), desc: translateInterfaceText("Equilíbrio ideal") },
            { value: 'fast' as AudienceSpeed, label: translateInterfaceText("Rápida"), desc: translateInterfaceText("Mais ativa") },
          ]).map((option) => {
            const isSelected = (audience.speed || 'medium') === option.value;
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => updateAudience('speed', option.value)}
                className={cn(
                  'flex flex-col items-center gap-1 px-3 py-3 rounded-xl border text-center transition-all duration-200',
                  isSelected
                    ? 'border-[#4B7BFF] bg-white shadow-sm'
                    : 'border-[#E4E7EC] bg-white hover:border-[#4B7BFF]/30'
                )}
              >
                <span className={cn(
                  'text-[13px] font-semibold',
                  isSelected ? 'text-[#1D4FFF]' : 'text-[#1A1A1A]'
                )}>
                  {option.label}
                </span>
                <span className="text-[10px] text-muted-foreground leading-tight">
                  {option.desc}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};
